// What the day screen shows, worked out from the day payload. Pure: no React,
// no clock, no I/O, so every rule below is unit-testable on its own.

import type { DayItem, DayResponse, DaySection, IsoDay } from '../api/client.ts';
import { weekdayName } from '../iso-day.ts';
import { wording } from '../shell/voice.ts';
import type { Voice } from '../shell/voice.ts';

export interface Progress {
  done: number;
  total: number;
}

/**
 * An item with steps is done when every step is done. The steps still show
 * their own count inside the item; they never become items of their own.
 */
export function itemDone(item: DayItem): boolean {
  if (item.steps && item.steps.length > 0) return item.steps.every((s) => s.done);
  return item.done;
}

/** Some steps done, but not all: the half state on the parent's circle. */
export function itemPartial(item: DayItem): boolean {
  if (!item.steps || item.steps.length === 0) return false;
  const done = item.steps.filter((s) => s.done).length;
  return done > 0 && done < item.steps.length;
}

export function stepsDone(item: DayItem): number {
  return item.steps ? item.steps.filter((s) => s.done).length : 0;
}

/**
 * ONE item, whatever it contains. A schedule with five steps counts as one
 * thing toward the day's figure - it keeps the day's number a count of things
 * you have to do rather than a measure of effort, and it stops one step-heavy
 * schedule dominating the whole day (AGENTS.md).
 */
export function sectionProgress(section: DaySection): Progress {
  return {
    done: section.items.filter(itemDone).length,
    total: section.items.length,
  };
}

export function dayProgress(sections: readonly DaySection[]): Progress {
  return sections.reduce<Progress>(
    (acc, section) => {
      const p = sectionProgress(section);
      return { done: acc.done + p.done, total: acc.total + p.total };
    },
    { done: 0, total: 0 },
  );
}

export function pct({ done, total }: Progress): number {
  return total === 0 ? 0 : Math.round((done / total) * 100);
}

/**
 * "3 things scheduled", and "1 thing scheduled".
 *
 * A future day with one item on it read "1 things scheduled" - inherited from
 * the prototype, which is why the prototype's copy is fixed too. A count is
 * exactly the place a missing plural rule shows, because the number that breaks
 * it is the commonest one there is.
 */
export function scheduledLine(total: number): string {
  return `${total} thing${total === 1 ? '' : 's'} scheduled`;
}

/**
 * The one sentence the day says back to you, under the bar.
 *
 * THE MAIN VOICE SURFACE, and the reason the summary line is drawn at every
 * completion rather than only at 100%: a Wording setting whose only visible
 * effect is a sentence you see once a day is a setting that appears broken
 * (`prototype-v2.md` §2.6).
 *
 * THE FUTURE LINE IS PLAIN IN BOTH. It is a statement about when something is
 * due - a fact about the calendar - and the approved pack has no entry for it.
 *
 * The voice is a REQUIRED argument rather than one defaulting to plain, here and
 * in every model below, for the reason `hueIn(hex, mode)` takes its mode: a
 * default would let a call site forget and never change, which is a screen that
 * silently ignores the setting rather than a type error.
 */
export function dayLine(sections: readonly DaySection[], future: boolean, voice: Voice): string {
  const p = dayProgress(sections);
  if (future) return `${p.total} scheduled. Nothing is due until then.`;
  if (p.done === p.total) return wording('day.allDone', voice);
  const left = p.total - p.done;
  if (left === 1) return wording('day.oneLeft', voice);
  const m = sections.length;
  return wording('day.someLeft', voice, { n: left, m, s: m === 1 ? '' : 's' });
}

/**
 * What to say when the + button is pressed at the schedule cap.
 *
 * BOTH NUMBERS COME FROM THE PAYLOAD. The cap is `section_cap()` in the
 * database - the same function the trigger enforces - so this sentence cannot
 * tell a user a maximum the database has stopped agreeing with. Restating "ten"
 * here is exactly the mistake AGENTS.md forbids, and it is why the day screen
 * drew no notice at all until the figures arrived.
 *
 * Null when there is room, which is every account until the tenth schedule.
 */
export function capNotice(schedules: { total: number; cap: number }): string | null {
  if (schedules.total < schedules.cap) return null;
  return (
    `You have ${schedules.total} schedules, the most Perfect Note allows. ` +
    'Archive one you are not using and you can add another straight away.'
  );
}

/**
 * The heading over an empty day, and WHICH DAY IT IS ALLOWED TO CLAIM.
 *
 * THE APPROVED PACK CARRIES THIRTEEN PAIRS AND THIS IS DELIBERATELY NOT A
 * FOURTEENTH. `empty.day.title`'s casual column is `nothing on today.` - the
 * captain's own reviewed wording, and true only OF TODAY. This panel is drawn
 * for whichever day the chooser is on, so on Yesterday, on Tomorrow and on every
 * day Month opens it would head a sentence naming a different day from the one
 * `emptyDayBody` names directly underneath it. A date is a FACT, and a fact
 * reads the same in both voices or the voice is changing more than words.
 *
 * So the casual line is used exactly where it is accurate, and the PLAIN heading
 * stands everywhere else. That is an APPROVED-COPY GAP rather than an
 * inconsistency: a day-neutral casual heading is a line the captain can
 * commission if they want one, and until they do, a casual reader meeting a
 * plain heading on a past day is mildly less consistent, while `nothing on
 * today.` above a Tuesday is the product telling them something untrue.
 *
 * WHICH DAY IS TODAY IS THE PAYLOAD'S ANSWER, never this client's. `day` and
 * `today` both arrive on `DayResponse`, resolved server-side by `todayIn()`; a
 * client working it out from a clock is the one thing that function exists to
 * prevent (AGENTS.md).
 */
export function emptyDayTitle(day: IsoDay, today: IsoDay, voice: Voice): string {
  return wording('empty.day.title', day === today ? voice : 'plain');
}

/**
 * A quiet day looks quiet: sections with nothing due do not appear at all, so
 * an empty day is a sentence rather than a screen full of zeroes.
 *
 * WHICH DAY IT IS DOES NOT MOVE BETWEEN THE VOICES - only its capitalisation
 * does, because lowercase openers are the casual register and a weekday is the
 * same weekday either way. This is the one place a caller of `wording` touches a
 * value on account of the voice, and it is a letter case rather than a fact.
 */
export function emptyDayBody(day: IsoDay, voice: Voice): string {
  const weekday = weekdayName(day);
  const named = weekday === '' ? 'This day' : weekday;
  const subject = voice === 'casual' ? named.toLowerCase() : named;
  return wording('empty.day.body', voice, { subject });
}

// ------------------------------------------------------------------ writes --
//
// A tick paints immediately and is sent afterwards, so these produce the next
// payload rather than mutating the one on screen. They are also how a write that
// did not land is put back, one row at a time: a ticked parent writes one row
// per step, and only the rows that failed may be moved back.

function recount(section: DaySection): DaySection {
  const p = sectionProgress(section);
  return { ...section, done: p.done, total: p.total };
}

function rebuild(day: DayResponse, sections: DaySection[]): DayResponse {
  const counted = sections.map(recount);
  return { ...day, sections: counted, progress: dayProgress(counted) };
}

/** Ticking an item with steps ticks all of its steps, as the design does. */
export function withItemDone(day: DayResponse, itemId: string, done: boolean): DayResponse {
  return rebuild(
    day,
    day.sections.map((section) => ({
      ...section,
      items: section.items.map((item) =>
        item.id === itemId
          ? { ...item, done, steps: item.steps?.map((s) => ({ ...s, done })) }
          : item,
      ),
    })),
  );
}

export function withStepDone(
  day: DayResponse,
  itemId: string,
  stepId: string,
  done: boolean,
): DayResponse {
  return rebuild(
    day,
    day.sections.map((section) => ({
      ...section,
      items: section.items.map((item) =>
        item.id === itemId
          ? { ...item, steps: item.steps?.map((s) => (s.id === stepId ? { ...s, done } : s)) }
          : item,
      ),
    })),
  );
}

/**
 * Ticking a reminder, or taking the tick back.
 *
 * It does NOT go through `rebuild`, and that is the decision rather than an
 * omission: reminders are deliberately not in the day's figure
 * (prototype-v2.md section 1.3), so a tick here must leave `3 of 7 done`
 * exactly where it was. Recounting would be the one line that quietly put them
 * in it.
 *
 * `completedDay` moves with `done` because the two are one fact: a reminder is
 * open exactly when it has no completion row, and which day that row carries is
 * what the meta line says. A payload where they disagreed would draw a ticked
 * row with no date on it.
 */
export function withReminderDone(day: DayResponse, reminderId: string, done: boolean): DayResponse {
  return {
    ...day,
    reminders: day.reminders.map((reminder) =>
      reminder.id === reminderId
        ? { ...reminder, done, completedDay: done ? day.day : null }
        : reminder,
    ),
  };
}

export function findItem(day: DayResponse, itemId: string): DayItem | null {
  for (const section of day.sections) {
    const item = section.items.find((i) => i.id === itemId);
    if (item) return item;
  }
  return null;
}

/**
 * Which completion rows a tick actually writes. An item is one row; an item
 * with steps is one row per step whose state is changing, because the steps are
 * what carry the state - the parent has none of its own.
 */
export function completionTargets(item: DayItem, done: boolean): string[] {
  if (item.steps && item.steps.length > 0) {
    return item.steps.filter((s) => s.done !== done).map((s) => s.id);
  }
  return item.done === done ? [] : [item.id];
}
